/**
 * ----------------------------------------------------------------------------------------------------
 * TIDIO CHAT — Prairie Wear customisations
 * ----------------------------------------------------------------------------------------------------
 * Loaded (deferred) from snippets/tidio-scroll-reveal.liquid. Two independent behaviours, both fully
 * self-guarding: if Tidio never loads, nothing here throws and nothing here does anything.
 *
 * 1. REVEAL ON SCROLL — all viewports
 *    The bubble appears only after 10% scroll, matching the Loox "Reviews" widget. This file only
 *    toggles the `itg-tidio-scrolled` class on <html>. The actual hiding is done by CSS that stays
 *    INLINE in the snippet, so the bubble can never flash before this file executes.
 *
 * 2. BUBBLE ONLY ON MOBILE — ≤749px
 *    Tidio's Flow ("First visit on site" trigger) pushes a bot greeting on arrival. On a phone the
 *    greeting swamps the viewport; the client wants the bubble alone. Tidio surfaces that greeting
 *    two different ways, so both are handled:
 *
 *      a) FLY MESSAGE (this is what Prairie Wear actually gets) — a preview card rendered while the
 *         chat is still CLOSED, so no `open` event fires and `close()` cannot touch it. Confirmed in
 *         DevTools as `<div data-testid="flyMessage" class="flyMessage …">`. Hidden by injecting a
 *         <style> into the widget's open shadow root, scoped to a mobile media query.
 *      b) FLOW AUTO-OPEN — the panel genuinely opens. Handled with Tidio's supported JS API:
 *         `on('open')` closes again while `userOpened` is false; `on('close')` resets the flag.
 *
 *    Desktop and tablet are untouched — the injected CSS is inside `@media (max-width: 749px)`, and
 *    the media query for the API path is re-read at event time so rotation is handled live.
 *
 * WHY NOT CLIP THE HOST: an earlier revision shrank #tidio-chat to an 84px box with `overflow:hidden`.
 * That needs `transform` on the host for the overflow to clip fixed-position children — but the
 * transform also makes the host their containing block, which re-anchored the fly message INSIDE the
 * 84px box and left it visibly cut in half next to the bubble. Do not reintroduce that approach.
 *
 * NOTE — unread badge: the bot message is still delivered, so Tidio may still show its unread count on
 * the bubble. Removing that means stopping the message at source (Tidio > Flows > restrict the "First
 * visit on site" trigger to desktop); theme code cannot suppress it. See context.md B32.
 * ----------------------------------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /* -- 1. Reveal on scroll ----------------------------------------------------------------------- */

  var THRESHOLD = 10; // percent scrolled before showing the bubble (same as the Loox reviews widget)
  var root = document.documentElement;

  function checkScroll() {
    var scrollHeight = document.documentElement.scrollHeight;
    if (!scrollHeight) return;
    var percent = ((window.scrollY + window.innerHeight) / scrollHeight) * 100;
    if (percent >= THRESHOLD) {
      root.classList.add('itg-tidio-scrolled');
    } else {
      root.classList.remove('itg-tidio-scrolled');
    }
  }

  checkScroll(); // handle reloads that restore a scrolled position
  window.addEventListener('scroll', checkScroll, { passive: true });
  window.addEventListener('resize', checkScroll, { passive: true });

  /* -- 2. Bubble only on mobile ------------------------------------------------------------------ */

  if (!window.matchMedia) return; // no media-query support: leave Tidio entirely alone

  var MOBILE_QUERY = '(max-width: 749px)';
  var MOBILE = window.matchMedia(MOBILE_QUERY);
  var HOST_ID = 'tidio-chat'; // Tidio's host element, which holds the open shadow root
  var STYLE_ID = 'itg-tidio-mobile-css';
  var OBSERVE_TIMEOUT_MS = 30000; // stop waiting for the widget after this
  var userOpened = false; // true only while an open the visitor started is still active
  var wired = false;

  /* 2a. Hide the fly-message preview card, mobile only, from inside the shadow root ---------------
     `data-testid` is listed first because a test hook is less likely to be renamed than a class.
     Scoped to a media query rather than Tidio's own `.mobile` class so the breakpoint stays ours. */

  var SHADOW_CSS =
    '@media ' +
    MOBILE_QUERY +
    '{' +
    '[data-testid="flyMessage"],.flyMessage{display:none !important;}' +
    '}';

  function injectShadowCss() {
    try {
      var host = document.getElementById(HOST_ID);
      if (!host || !host.shadowRoot) return false;
      if (host.shadowRoot.getElementById(STYLE_ID)) return true; // already done

      var style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = SHADOW_CSS;
      host.shadowRoot.appendChild(style);
      return true;
    } catch (e) {
      return false; // never break the page over this
    }
  }

  if (!injectShadowCss()) {
    // The widget mounts asynchronously. Watch for it, then stop watching — no polling loop.
    try {
      var observer = new MutationObserver(function () {
        if (injectShadowCss()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      window.setTimeout(function () {
        observer.disconnect();
      }, OBSERVE_TIMEOUT_MS);
    } catch (e) {}

    // Shadow-root mutations do not reach a document observer, so also retry on Tidio's own ready
    // event and once on a short delay — covers the host existing before its shadow root is populated.
    document.addEventListener('tidioChat-ready', injectShadowCss, { once: true });
    window.setTimeout(injectShadowCss, 3000);
  }

  /* 2b. Close a genuine flow auto-open (supported JS API) ---------------------------------------- */

  /**
   * A pointerdown anywhere inside the widget counts as intent to open. The widget lives in a shadow
   * root, so at document level the event is retargeted to the host; we read the composed path when
   * available and fall back to the retargeted target. Capture phase, so we still see the event if
   * Tidio stops propagation; passive, since we never call preventDefault.
   */
  document.addEventListener(
    'pointerdown',
    function (event) {
      try {
        var host = document.getElementById(HOST_ID);
        if (!host) return;
        var path = typeof event.composedPath === 'function' ? event.composedPath() : null;
        var inside = path
          ? path.indexOf(host) !== -1
          : host === event.target || host.contains(event.target);
        if (inside) userOpened = true;
      } catch (e) {}
    },
    { capture: true, passive: true }
  );

  function wireTidio() {
    if (wired) return;

    var api = window.tidioChatApi;
    if (!api || typeof api.on !== 'function') return; // not ready yet (or Tidio absent)
    wired = true;

    try {
      api.on('open', function () {
        if (!MOBILE.matches) return; // desktop / tablet: leave Tidio's own behaviour alone
        if (userOpened) return; // genuine tap on the bubble — let it open
        try {
          api.close();
        } catch (e) {}
      });

      api.on('close', function () {
        userOpened = false;
      });
    } catch (e) {
      /* API shape changed — fail silently rather than break the bubble */
    }
  }

  wireTidio(); // API may already be ready by the time this deferred file runs
  document.addEventListener('tidioChat-ready', wireTidio, { once: true });
  window.setTimeout(wireTidio, 3000); // single-shot race guard — deliberately not a polling loop
})();
